import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Gavel, Plus } from '@phosphor-icons/react';
import { API } from '@/App';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const STATUS_STYLE = {
  open: 'bg-yellow-100 text-yellow-700',
  under_review: 'bg-blue-100 text-blue-700',
  awaiting_evidence: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

const Disputes = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ booking_id: '', reason: '', description: '' });

  useEffect(() => {
    fetch(`${API}/disputes`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => { setRows(d.disputes || []); setLoading(false); });
    const b = search.get('booking');
    if (b) {
      setForm((p) => ({ ...p, booking_id: b }));
      setShowNew(true);
    }
  }, [search]);

  const submit = async () => {
    if (!form.booking_id || !form.reason) {
      toast.error('Booking ID and reason are required.');
      return;
    }
    const res = await fetch(`${API}/disputes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(form),
    });
    const data = await res.json();
    if (!res.ok) { toast.error(typeof data.detail === 'string' ? data.detail : 'Failed'); return; }
    toast.success('Dispute opened');
    navigate(`/disputes/${data.dispute_id}`);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="my-disputes-title">
            My Disputes
          </h1>
          <Button onClick={() => setShowNew(true)} className="dropio-gradient-cta text-white" data-testid="open-new-dispute">
            <Plus size={16} weight="bold" className="mr-1" /> Open Dispute
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-8">Open and historic disputes related to your bookings.</p>

        {showNew && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="new-dispute-form">
            <h2 className="font-bold text-lg mb-3">Open a new dispute</h2>
            <div className="space-y-3">
              <div>
                <Label>Booking ID</Label>
                <Input value={form.booking_id} onChange={(e) => setForm({ ...form, booking_id: e.target.value })}
                       placeholder="booking_xxxxxxxxxxxx" data-testid="new-dispute-booking" />
              </div>
              <div>
                <Label>Reason</Label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                       placeholder="e.g., Parcel damaged on delivery" data-testid="new-dispute-reason" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                          placeholder="Provide details — what happened, when, and what outcome you expect."
                          data-testid="new-dispute-description" />
              </div>
              <div className="flex gap-2">
                <Button onClick={submit} className="dropio-gradient-cta text-white" data-testid="new-dispute-submit">Open</Button>
                <Button onClick={() => setShowNew(false)} className="bg-white text-gray-900 border border-gray-200">Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center" data-testid="disputes-empty">
            <Gavel size={40} weight="duotone" className="mx-auto mb-3 text-gray-300" />
            <p className="font-semibold text-gray-900">No disputes</p>
            <p className="text-sm text-gray-500">You can open one from a paid booking on your dashboard.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-disputes-list">
            {rows.map((d) => (
              <Link key={d.dispute_id} to={`/disputes/${d.dispute_id}`} className="block bg-white rounded-xl border border-gray-200 hover:border-[#6D28D9]/50 p-4 transition" data-testid={`my-dispute-${d.dispute_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{d.reason}</p>
                    <p className="text-xs text-gray-500 font-mono mt-0.5">Booking {d.booking_id}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_STYLE[d.status] || 'bg-gray-100'}`}>
                    {d.status?.replace(/_/g, ' ')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Disputes;
